import { useEffect, useState, useMemo, memo } from "react";

import { useEffectOnce } from "shared/lib/hooks";
import { InfiniteScrollWithObserver } from "shared/ui";
import { AccountHistoryItemI } from "shared/lib/interfaces/account";

import { useAccountDetailsContext } from "@/modules/pipeline/account-details/context";
import { HistoryList } from "./history-list";
import { CUSTOM_EVENTS } from "@/constants/custom-events";
import {
  aiEmailGenerationIsCompletedCondition,
  aiIsGeneratingEmailCondition,
} from "@/components/modules/pipeline/account-details/tabs/tab-history/utils";
import { usePollActivityLogs } from "@/components/modules/pipeline/account-details/tabs/tab-history/hooks";
import { Container } from "@/components/base/container";
import { LoadingComponent } from "@/components/modules/loading";
import { AccountDetailsTabHistoryAddAccountNote } from "@/modules/pipeline/account-details/tabs/tab-history/add-account-note";
import { BookedMeetingCalendarEvent } from "@/modules/pipeline/account-details/shared/booked-meeting-calendar-event";
import { checkIfAccountShouldShowCalendarEventStatus } from "@/modules/pipeline/account-details/shared/booked-meeting-calendar-event/utils";

interface BookedMeetingStatusAddonRawPropsI {
  activityLog: AccountHistoryItemI;
}

const BookedMeetingStatusAddonRaw = ({
  activityLog,
}: BookedMeetingStatusAddonRawPropsI) => {
  const { accountStatus, account, campaign } = useAccountDetailsContext();

  if (
    checkIfAccountShouldShowCalendarEventStatus(accountStatus) &&
    activityLog.follow_up_time &&
    activityLog.follow_up_time === accountStatus?.meeting_time
  ) {
    return (
      <div className="relative -mt-2 ml-12">
        <BookedMeetingCalendarEvent
          campaignId={campaign?.id}
          accountId={account?.id}
        />
      </div>
    );
  }

  return null;
};
const BookedMeetingStatusAddon = memo(BookedMeetingStatusAddonRaw);

export const AccountDetailsTabHistory = () => {
  const {
    accountHistoryData: {
      data: accountHistoryRaw,
      loadMore: loadMoreAccountHistory,
      reloadData: reloadAccountHistory,
      isReachedEnd: isReachAccountHistoryEnd,
      isLoading: isLoadingAccountHistory,
    },
  } = useAccountDetailsContext();

  const [accountHistory, setAccountHistory] =
    useState<AccountHistoryItemI[]>(accountHistoryRaw);

  const logsToPoll = useMemo(() => {
    if (!accountHistory || accountHistory.length === 0) {
      return [];
    }

    return accountHistory
      .filter(aiIsGeneratingEmailCondition)
      .map((activity) => ({
        activityLogId: activity.id,
        until: aiEmailGenerationIsCompletedCondition,
      }));
  }, [accountHistory]);

  usePollActivityLogs({ config: logsToPoll, reducer: setAccountHistory });

  useEffect(() => {
    setAccountHistory(accountHistoryRaw);
  }, [accountHistoryRaw]);

  // Need to reload account history if user adds a note from the widget dialer
  useEffectOnce(() => {
    reloadAccountHistory();

    window.document.addEventListener(
      CUSTOM_EVENTS.ACCOUNT.HISTORY.RELOAD,
      reloadAccountHistory
    );

    return () => {
      window.document.removeEventListener(
        CUSTOM_EVENTS.ACCOUNT.HISTORY.RELOAD,
        reloadAccountHistory
      );
    };
  });

  return (
    <Container className="min-w-[0px] pb-32 pt-12">
      <AccountDetailsTabHistoryAddAccountNote className="mx-auto mb-12 max-w-[1024px]" />

      <InfiniteScrollWithObserver
        isLoading={isLoadingAccountHistory}
        loadingComponent={<LoadingComponent delay={300} />}
        isMore={!isReachAccountHistoryEnd}
        onMore={loadMoreAccountHistory}
        isDefaultMessageHidden={accountHistory.length === 0}
      >
        <HistoryList
          className="mx-auto max-w-[1024px]"
          isLoading={isLoadingAccountHistory}
          historyData={accountHistory}
          activityLogAddons={{
            BookedMeetingEvent: BookedMeetingStatusAddon,
          }}
        />
      </InfiniteScrollWithObserver>
    </Container>
  );
};
